import { useCallback, useEffect, useRef, useState } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Save, Play, Pause, FileImage, Keyboard } from "lucide-react";
import { toast } from "sonner";

import { SCHEMES, TOOLS, FORMATION_TEMPLATES } from "../lib/tactical/constants";
import { buildInitialFrames, cloneFrame, newFrame } from "../lib/tactical/frames";
import { usePlayback } from "../lib/tactical/usePlayback";
import { exportFramesToGif } from "../lib/tactical/gifExport";

import TacticalCanvas from "../components/tactical/TacticalCanvas";
import ToolPalette from "../components/tactical/ToolPalette";
import FrameTimeline from "../components/tactical/FrameTimeline";
import FormationsList from "../components/tactical/FormationsList";

const HISTORY_LIMIT = 40;

export default function TacticalBoardPage() {
  const { activeTeam } = useTeams();
  const [formations, setFormations] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [name, setName] = useState("Nuovo schema");
  const [scheme, setScheme] = useState("4-4-2");

  const [frames, setFrames] = useState(buildInitialFrames("4-4-2"));
  const [frameIdx, setFrameIdx] = useState(0);
  const [tool, setTool] = useState("select");
  const [color, setColor] = useState("#ffffff");
  const [exporting, setExporting] = useState(false);
  const [exportingPng, setExportingPng] = useState(false);
  const [isPresentation, setIsPresentation] = useState(false);
  const pageRef = useRef(null);

  // Undo/redo history of `frames` snapshots
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const skipNextHistoryPush = useRef(false);

  const refreshHistoryFlags = () => {
    setCanUndo(undoStack.current.length > 0);
    setCanRedo(redoStack.current.length > 0);
  };

  /** Wrap any frames mutation with history bookkeeping. */
  const setFramesWithHistory = useCallback((updater) => {
    setFrames((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (next === prev) return prev;
      if (skipNextHistoryPush.current) {
        skipNextHistoryPush.current = false;
      } else {
        undoStack.current.push(prev);
        if (undoStack.current.length > HISTORY_LIMIT) undoStack.current.shift();
        redoStack.current = [];
        // Defer flag refresh to next tick so React has finished its render cycle
        queueMicrotask(refreshHistoryFlags);
      }
      return next;
    });
  }, []);

  const undo = useCallback(() => {
    if (undoStack.current.length === 0) return;
    setFrames((prev) => {
      redoStack.current.push(prev);
      const next = undoStack.current.pop();
      skipNextHistoryPush.current = true;
      queueMicrotask(refreshHistoryFlags);
      return next;
    });
  }, []);

  const redo = useCallback(() => {
    if (redoStack.current.length === 0) return;
    setFrames((prev) => {
      undoStack.current.push(prev);
      const next = redoStack.current.pop();
      skipNextHistoryPush.current = true;
      queueMicrotask(refreshHistoryFlags);
      return next;
    });
  }, []);

  const { isPlaying, previewFrame, segmentIdx, play, stop } = usePlayback(frames);

  const currentFrame = frames[frameIdx] || frames[0];
  const renderFrame = previewFrame || currentFrame;

  const load = useCallback(async () => {
    if (!activeTeam) return;
    const { data } = await api.get(`/teams/${activeTeam.id}/formations`);
    setFormations(data);
  }, [activeTeam]);

  useEffect(() => { load(); }, [load]);

  // Keyboard shortcuts (only when not typing in inputs)
  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement;
      return el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
    };
    const onKey = (e) => {
      if (isTyping()) return;
      const isMod = e.ctrlKey || e.metaKey;
      if (isMod && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
        return;
      }
      if (isMod && e.key.toLowerCase() === "y") {
        e.preventDefault();
        redo();
        return;
      }
      if (e.key === "Escape" && isPresentation) {
        setIsPresentation(false);
        return;
      }
      if (e.key === " ") { e.preventDefault(); isPlaying ? stop() : tryPlay(); return; }
      if (e.key === "ArrowLeft") { setFrameIdx((i) => Math.max(0, i - 1)); return; }
      if (e.key === "ArrowRight") { setFrameIdx((i) => Math.min(frames.length - 1, i + 1)); return; }
      const map = Object.fromEntries(TOOLS.map((t) => [t.shortcut.toLowerCase(), t.id]));
      const id = map[e.key.toLowerCase()];
      if (id && !isPlaying) setTool(id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isPlaying, frames.length, isPresentation, undo, redo]);

  // Sync presentation state with system fullscreen events (must be before any early return)
  useEffect(() => {
    const onFs = () => {
      if (!document.fullscreenElement) setIsPresentation(false);
    };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  // ---------- Frame mutations (with history) ----------
  const patchFrame = (patch) => {
    setFramesWithHistory((prev) => prev.map((f, i) => (i === frameIdx ? { ...f, ...patch } : f)));
  };
  const setPlayers = (updater) => {
    const players = typeof updater === "function" ? updater(currentFrame.players) : updater;
    patchFrame({ players });
  };
  const setDrawings = (updater) => {
    const drawings = typeof updater === "function" ? updater(currentFrame.drawings) : updater;
    patchFrame({ drawings });
  };

  // ---------- Formation CRUD ----------
  const newBoard = () => {
    stop();
    setCurrentId(null);
    setName("Nuovo schema");
    setScheme("4-4-2");
    setFramesWithHistory(buildInitialFrames("4-4-2"));
    setFrameIdx(0);
  };
  const loadFormation = (f) => {
    stop();
    setCurrentId(f.id);
    setName(f.name);
    setScheme(f.scheme || "4-4-2");
    if (f.frames && f.frames.length > 0) setFramesWithHistory(f.frames);
    else setFramesWithHistory([newFrame("Start", f.players || [], f.drawings || [])]);
    setFrameIdx(0);
  };
  const applyScheme = (s) => {
    setScheme(s);
    const startPlayers = SCHEMES[s].map((p, i) => ({ ...p, id: i, color: "#0ea5e9" }));
    setFramesWithHistory([newFrame("Start", startPlayers, [])]);
    setFrameIdx(0);
  };
  const save = async () => {
    const f0 = frames[0] || { players: [], drawings: [] };
    const payload = {
      team_id: activeTeam.id, name, scheme,
      players: f0.players, drawings: f0.drawings, frames,
    };
    if (currentId) await api.put(`/formations/${currentId}`, payload);
    else {
      const { data } = await api.post("/formations", payload);
      setCurrentId(data.id);
    }
    toast.success(`Schema salvato (${frames.length} frame)`);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare schema?")) return;
    await api.delete(`/formations/${id}`);
    if (currentId === id) newBoard();
    load();
  };

  // ---------- Canvas callbacks ----------
  const handleMovePlayer = (id, pt) =>
    setPlayers((prev) => prev.map((p) => (p.id === id ? { ...p, x: pt.x, y: pt.y } : p)));
  const handleRemovePlayer = (id) =>
    setPlayers((prev) => prev.filter((p) => p.id !== id));
  const handleRenamePlayer = (id, value) =>
    setPlayers((prev) => prev.map((p) => (p.id === id ? { ...p, name: value || null } : p)));
  const handleAddDrawing = (d) => setDrawings((prev) => [...prev, d]);
  const handleRemoveDrawing = (idx) => setDrawings((prev) => prev.filter((_, i) => i !== idx));
  const handleAddText = (t) => setDrawings((prev) => [...prev, { type: "text", ...t }]);
  const handleAddGoal = (g) =>
    setDrawings((prev) => [...prev, { id: crypto.randomUUID(), type: "goal", ...g }]);

  const addPlayer = () => {
    const id = (currentFrame.players.reduce((m, p) => Math.max(m, p.id), 0) || 0) + 1;
    setPlayers([...currentFrame.players, { id, x: 50, y: 50, label: String(id), color }]);
  };
  const clearAllPlayers = () => {
    if (window.confirm("Rimuovere tutte le pedine?")) setPlayers([]);
  };

  // ---------- Frame management ----------
  const addFrame = () => {
    setFramesWithHistory((prev) => {
      const base = prev[frameIdx];
      const next = cloneFrame(base);
      next.name = `Frame ${prev.length + 1}`;
      const out = [...prev];
      out.splice(frameIdx + 1, 0, next);
      return out;
    });
    setFrameIdx((i) => i + 1);
  };
  const removeFrame = (idx) => {
    if (frames.length <= 1) return toast.error("Deve restare almeno un frame");
    setFramesWithHistory((prev) => prev.filter((_, i) => i !== idx));
    setFrameIdx((i) => Math.max(0, Math.min(i, frames.length - 2)));
  };
  const moveFrame = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= frames.length) return;
    setFramesWithHistory((prev) => {
      const out = [...prev];
      const [moved] = out.splice(idx, 1);
      out.splice(j, 0, moved);
      return out;
    });
    if (frameIdx === idx) setFrameIdx(j);
  };
  const setFrameDuration = (idx, ms) =>
    setFramesWithHistory((prev) => prev.map((f, i) => (i === idx ? { ...f, duration_ms: Math.max(200, Number(ms) || 0) } : f)));
  const setFrameName = (idx, value) =>
    setFramesWithHistory((prev) => prev.map((f, i) => (i === idx ? { ...f, name: value } : f)));

  // ---------- Playback ----------
  const tryPlay = () => {
    if (!play()) toast.info("Aggiungi almeno 2 frame per animare");
  };

  // ---------- GIF export ----------
  const onExportGif = async () => {
    if (frames.length < 2) return toast.info("Aggiungi almeno 2 frame per esportare GIF");
    try {
      setExporting(true);
      const blob = await exportFramesToGif(frames);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(name || "schema").replace(/\s+/g, "-")}.gif`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("GIF esportato");
    } catch (err) {
      console.error(err);
      toast.error("Errore export GIF");
    } finally {
      setExporting(false);
    }
  };

  // ---------- PNG export (current frame) ----------
  const onExportPng = async () => {
    const svg = document.querySelector('[data-testid="pitch-svg"]');
    if (!svg) return toast.error("Canvas non trovato");
    try {
      setExportingPng(true);
      // Wrap SVG in proper xml declaration with computed viewBox
      const xml = new XMLSerializer().serializeToString(svg);
      const fullXml = `<?xml version="1.0" encoding="UTF-8"?>${xml}`;
      const blob = new Blob([fullXml], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      const pngBlob = await new Promise((resolve, reject) => {
        img.onload = () => {
          const W = 1600;
          const H = 1600;
          const canvas = document.createElement("canvas");
          canvas.width = W;
          canvas.height = H;
          const ctx = canvas.getContext("2d");
          // Pitch green background fill
          ctx.fillStyle = "#15803d";
          ctx.fillRect(0, 0, W, H);
          ctx.drawImage(img, 0, 0, W, H);
          // Watermark
          ctx.fillStyle = "rgba(255,255,255,0.55)";
          ctx.font = "bold 26px system-ui, sans-serif";
          ctx.textAlign = "right";
          ctx.fillText(`${name || "Schema"} · Calcio Club Manager`, W - 24, H - 18);
          canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("canvas toBlob"))), "image/png");
        };
        img.onerror = reject;
        img.src = url;
      });
      const dl = URL.createObjectURL(pngBlob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `${(name || "schema").replace(/\s+/g, "-")}.png`;
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        URL.revokeObjectURL(dl);
      }, 5000);
      toast.success("Immagine esportata");
    } catch (err) {
      console.error(err);
      toast.error("Errore export PNG");
    } finally {
      setExportingPng(false);
    }
  };

  // ---------- Presentation mode ----------
  const togglePresentation = async () => {
    const el = pageRef.current;
    try {
      if (!isPresentation) {
        if (el && el.requestFullscreen) await el.requestFullscreen();
        setIsPresentation(true);
      } else {
        if (document.fullscreenElement && document.exitFullscreen) await document.exitFullscreen();
        setIsPresentation(false);
      }
    } catch (_) {
      // Fallback: just toggle the visual mode without real fullscreen
      setIsPresentation((v) => !v);
    }
  };
  // Sync state with system fullscreen events
  // (moved above the early return to keep hook order stable)
  const activeTool = TOOLS.find((t) => t.id === tool);

  return (
    <div
      ref={pageRef}
      className={`space-y-6 ${isPresentation ? "bg-slate-900 p-6 min-h-screen" : ""}`}
      data-testid="tactical-page"
    >
      {/* Header — hidden in presentation mode */}
      {!isPresentation && (
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow">Lavagna Tattica</div>
            <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Schema · Animazione</h1>
            <p className="text-sm text-slate-500 mt-1 hidden sm:flex items-center gap-2 flex-wrap">
              <Keyboard size={12} />
              <span className="font-mono">Spazio</span> play/stop ·
              <span className="font-mono">←→</span> frame ·
              <span className="font-mono">Ctrl+Z/Y</span> undo/redo ·
              <span className="font-mono">doppio-click pedina</span> rinomina
            </p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-56"
              data-testid="formation-name"
              placeholder="Nome schema"
            />
            <Select value={scheme} onValueChange={applyScheme}>
              <SelectTrigger className="w-28" data-testid="scheme-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.keys(SCHEMES).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={newBoard} variant="outline" data-testid="new-board-btn">
              <Plus size={14} className="mr-1" /> Nuovo
            </Button>
            {!isPlaying ? (
              <Button onClick={tryPlay} variant="outline" data-testid="play-btn">
                <Play size={14} className="mr-1" /> Play
              </Button>
            ) : (
              <Button onClick={stop} variant="outline" data-testid="stop-btn" className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100">
                <Pause size={14} className="mr-1" /> Stop
              </Button>
            )}
            <Button onClick={onExportGif} variant="outline" disabled={exporting} data-testid="export-gif-btn">
              <FileImage size={14} className="mr-1" /> {exporting ? "Genero..." : "GIF"}
            </Button>
            <Button onClick={save} className="bg-sky-500 hover:bg-sky-600" data-testid="save-formation-btn">
              <Save size={14} className="mr-1" /> Salva
            </Button>
          </div>
        </div>
      )}

      <div className={`grid grid-cols-1 ${isPresentation ? "" : "lg:grid-cols-[1fr_300px]"} gap-6`}>
        {/* Toolbar + Pitch + Timeline */}
        <Card className={`p-4 border-slate-200 ${isPresentation ? "bg-slate-900 border-slate-700 text-white" : ""}`}>
          <ToolPalette
            tool={tool}
            setTool={setTool}
            color={color}
            setColor={setColor}
            disabled={isPlaying}
            onClearDrawings={() => setDrawings([])}
            onClearPlayers={clearAllPlayers}
            onAddPlayer={addPlayer}
            onUndo={undo}
            onRedo={redo}
            canUndo={canUndo}
            canRedo={canRedo}
            onExportPng={onExportPng}
            exporting={exportingPng}
            onTogglePresentation={togglePresentation}
            isPresentation={isPresentation}
            templates={FORMATION_TEMPLATES}
            onApplyTemplate={applyScheme}
          />

          {activeTool && !isPlaying && (
            <div className="mb-3 text-xs text-slate-500 px-1 -mt-2">
              <span className="font-semibold text-slate-700">{activeTool.label}:</span> {activeTool.hint}
            </div>
          )}

          <TacticalCanvas
            frame={renderFrame}
            tool={tool}
            color={color}
            isPlaying={isPlaying}
            segmentIdx={segmentIdx}
            framesTotal={frames.length}
            onMovePlayer={handleMovePlayer}
            onRemovePlayer={handleRemovePlayer}
            onRenamePlayer={handleRenamePlayer}
            onAddDrawing={handleAddDrawing}
            onRemoveDrawing={handleRemoveDrawing}
            onAddText={handleAddText}
            onAddGoal={handleAddGoal}
          />

          <FrameTimeline
            frames={frames}
            frameIdx={frameIdx}
            setFrameIdx={setFrameIdx}
            onAddFrame={addFrame}
            onRemoveFrame={removeFrame}
            onMoveFrame={moveFrame}
            onSetFrameName={setFrameName}
            onSetFrameDuration={setFrameDuration}
            disabled={isPlaying}
          />
          {!isPresentation && (
            <p className="text-xs text-slate-500 mt-2">
              Modifica le pedine sul frame selezionato. La durata indica quanto dura la transizione verso il frame successivo.
            </p>
          )}
        </Card>

        {/* Saved formations (hidden in presentation mode) */}
        {!isPresentation && (
          <Card className="p-4 border-slate-200 h-fit">
            <div className="label-eyebrow mb-3">Schemi salvati</div>
            <FormationsList
              formations={formations}
              currentId={currentId}
              onLoad={loadFormation}
              onRemove={remove}
            />
          </Card>
        )}
      </div>
    </div>
  );
}
